import { type RankQuestionBlock } from "@/q-n-a.ts";
import { Paper, Stack, Text, Button, Group, Badge, ActionIcon } from '@mantine/core';
import { useState, useEffect } from 'react';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical, IconChevronUp, IconChevronDown } from '@tabler/icons-react';

interface RankQuestionProps {
    question: RankQuestionBlock;
    onChange: (question: RankQuestionBlock) => void;
    onProgress: (qid: string | number) => void;
}

function RankedItem({ 
    option, 
    index, 
    rank, 
    onMoveUp, 
    onMoveDown 
}: { 
    option: string; 
    index: number; 
    rank: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: `ranked-${index}`,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            p="sm"
            withBorder
            radius="md"
            {...attributes}
        >
            <Group gap="xs" wrap="nowrap">
                <div {...listeners} style={{ cursor: 'grab', touchAction: 'none' }}>
                    <IconGripVertical size={18} stroke={1.5} />
                </div>
                <Badge size="lg" variant="filled" color="blue" radius="md">
                    {rank}
                </Badge>
                <Text flex={1} size="sm" fw={500}>
                    {option}
                </Text>
                <Group gap={4}>
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={onMoveUp}
                        disabled={rank === 1}
                    >
                        <IconChevronUp size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={onMoveDown}
                        disabled={rank === 3}
                    >
                        <IconChevronDown size={16} />
                    </ActionIcon>
                </Group>
            </Group>
        </Paper>
    );
}

export default function RankQuestion({ question, onChange, onProgress }: RankQuestionProps) {
    // Parse existing answer if available (format: "1-2-3" where numbers are 1-indexed option indices)
    const getInitialSelected = (): number[] => {
        if (question.answer && typeof question.answer === 'string') {
            return question.answer.split('-').map(n => parseInt(n, 10) - 1);
        }
        return [];
    };

    const [selectedIndices, setSelectedIndices] = useState<number[]>(getInitialSelected);
    const [rankedIndices, setRankedIndices] = useState<number[]>(selectedIndices);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor)
    );

    // Update ranked indices when selected indices change
    useEffect(() => {
        setRankedIndices(selectedIndices);
    }, [selectedIndices]);

    // Update answer when ranking changes
    useEffect(() => {
        if (rankedIndices.length === 3) {
            // Format as "1-2-3" (1-indexed)
            const answerString = rankedIndices.map(idx => idx + 1).join('-');
            const updatedQuestion: RankQuestionBlock = {
                ...question,
                answer: answerString,
                answerLog: {
                    ...question.answerLog,
                    [Date.now()]: answerString,
                },
            };
            onChange(updatedQuestion);
            onProgress(question.qid);
        } else if (rankedIndices.length === 0) {
            // Clear answer if nothing selected
            const updatedQuestion: RankQuestionBlock = {
                ...question,
                answer: undefined,
            };
            onChange(updatedQuestion);
        }
    }, [rankedIndices]);

    const handleOptionClick = (optionIndex: number) => {
        if (selectedIndices.includes(optionIndex)) {
            // Deselect
            const newSelected = selectedIndices.filter(idx => idx !== optionIndex);
            setSelectedIndices(newSelected);
            setRankedIndices(newSelected);
        } else if (selectedIndices.length < 3) {
            // Select (add to end of ranking)
            const newSelected = [...selectedIndices, optionIndex];
            setSelectedIndices(newSelected);
            setRankedIndices(newSelected);
        }
        // If already have 3 selected, do nothing (user must deselect first)
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const activeIndex = rankedIndices.findIndex((_, i) => `ranked-${i}` === active.id);
        const overIndex = rankedIndices.findIndex((_, i) => `ranked-${i}` === over.id);

        if (activeIndex !== -1 && overIndex !== -1) {
            setRankedIndices(arrayMove(rankedIndices, activeIndex, overIndex));
        }
    };

    const handleMoveUp = (index: number) => {
        if (index > 0) {
            const newRanked = [...rankedIndices];
            [newRanked[index - 1], newRanked[index]] = [newRanked[index], newRanked[index - 1]];
            setRankedIndices(newRanked);
        }
    };

    const handleMoveDown = (index: number) => {
        if (index < rankedIndices.length - 1) {
            const newRanked = [...rankedIndices];
            [newRanked[index], newRanked[index + 1]] = [newRanked[index + 1], newRanked[index]];
            setRankedIndices(newRanked);
        }
    };

    const availableOptions = question.options.map((option, index) => ({
        text: option,
        index,
        isSelected: selectedIndices.includes(index),
    }));

    return (
        <Paper p="md" withBorder radius="lg">
            <Stack gap="md">
                <Text size="1.1rem" fw={500}>
                    {question.question}
                </Text>

                {/* Selected and Ranked Items */}
                {rankedIndices.length > 0 && (
                    <Stack gap="xs">
                        <Text size="sm" c="dimmed" fw={500}>
                            Your Top 3 (drag to reorder):
                        </Text>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={rankedIndices.map((_, i) => `ranked-${i}`)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Stack gap="xs">
                                    {rankedIndices.map((optionIndex, rank) => (
                                        <RankedItem
                                            key={`ranked-${optionIndex}`}
                                            option={question.options[optionIndex]}
                                            index={optionIndex}
                                            rank={rank + 1}
                                            onMoveUp={() => handleMoveUp(rank)}
                                            onMoveDown={() => handleMoveDown(rank)}
                                        />
                                    ))}
                                </Stack>
                            </SortableContext>
                        </DndContext>
                    </Stack>
                )}

                {/* Available Options */}
                <Stack gap="xs">
                    <Text size="sm" c="dimmed" fw={500}>
                        {selectedIndices.length < 3
                            ? `Select ${3 - selectedIndices.length} more option${3 - selectedIndices.length > 1 ? 's' : ''}:`
                            : 'All options selected. Click to deselect:'}
                    </Text>
                    <Group gap="xs" wrap="wrap">
                        {availableOptions.map(({ text, index, isSelected }) => (
                            <Button
                                key={index}
                                variant={isSelected ? 'filled' : 'outline'}
                                color={isSelected ? 'blue' : 'gray'}
                                size="sm"
                                onClick={() => handleOptionClick(index)}
                                disabled={!isSelected && selectedIndices.length >= 3}
                                style={{ flex: '1 1 auto', minWidth: '120px' }}
                            >
                                {text}
                            </Button>
                        ))}
                    </Group>
                </Stack>
            </Stack>
        </Paper>
    );
}

